import type { SetStateAction } from 'react';
import type { Action } from 'react-sweet-state';

import { DEFAULT_PAYLOAD } from './constants';
import type { Name, Payload, State } from './types';

/* Containers would be a more idiomatic approach to resetting scoped data in sweet-state.
 * However, the current architecture makes it impractical to find a common parent for the Container
 * among the multitude of synchronised scroll views.
 */
export const resetPayload =
	(name: Name): Action<State> =>
	({ setState }) =>
		setState({ [name]: DEFAULT_PAYLOAD });

export const setPayload =
	(name: Name, update: SetStateAction<Payload>): Action<State> =>
	({ getState, setState }) =>
		setState({
			[name]: typeof update === 'function' ? update(getState()[name] ?? DEFAULT_PAYLOAD) : update,
		});

/* Not every scroll element will be mounted for the synchronisation lifetime.
 * As such, we need to update the scroll property of newly added elements
 * to the existing stored offset.
 */
export const addScrollElement =
	(name: Name, element: HTMLElement): Action<State> =>
	({ dispatch }) => {
		dispatch(
			setPayload(name, ({ scrollElements, ...data }) => {
				// eslint-disable-next-line no-param-reassign
				element.scrollLeft = data.offset;
				return {
					...data,
					scrollElements: [...scrollElements, element],
				};
			}),
		);
	};

export const removeScrollElement =
	(name: Name, element: HTMLElement): Action<State> =>
	({ dispatch }) => {
		dispatch(
			setPayload(name, ({ scrollElements, ...data }) => ({
				...data,
				scrollElements: scrollElements.filter((ele) => ele !== element),
			})),
		);
	};

/* This action is used in conjunction with event handlers that do *not* read the scroll offset directly. For example,
 * we use wheel events as the primary driver of our scroll synchronisation, since they demonstrate better performance.
 *
 * We make two assumption about such event handlers though:
 * 1. They cannot be triggered at the same time as a resize event
 * 2. The offset needs to be manually contained within the overflow container boundaries
 *
 * If the first assumption does not hold true, bugs are abound. Resize events (from a ResizeObserver) occur after scroll,
 * which would mean that the boundaries we use to contain the offset are from the previous frame.
 */
export const setOffset =
	(name: Name, update: SetStateAction<Payload['offset']>): Action<State> =>
	({ dispatch }) => {
		dispatch(
			setPayload(name, ({ offset, viewportWidth, scrollWidth, scrollElements }) => {
				const updatedOffset = typeof update === 'function' ? update(offset) : update;
				const saneOffset = Math.max(Math.min(updatedOffset, scrollWidth - viewportWidth), 0);

				scrollElements.forEach((node) => {
					// eslint-disable-next-line no-param-reassign
					node.scrollLeft = saneOffset;
				});

				return {
					offset: saneOffset,
					scrollWidth,
					viewportWidth,
					scrollElements,
				};
			}),
		);
	};

/* This action is used in conjunction with explicit scroll events, and exists for edge cases not covered
 * by the above action (setOffset). For example, wheel events do not fire for:
 * 1) Scrolling via the keyboard, such as when using the arrow keys
 * 2) Programmatic changes to the scroll offset, such as the scrollIntoView API
 */
export const setOffsetFallback =
	(name: Name, updatedOffset: number): Action<State> =>
	({ getState, dispatch }) => {
		dispatch(
			setPayload(name, ({ offset, viewportWidth, scrollWidth, scrollElements }) => {
				if (updatedOffset === offset) {
					return getState()[name];
				}

				scrollElements.forEach((node) => {
					// eslint-disable-next-line no-param-reassign
					node.scrollLeft = updatedOffset;
				});

				return {
					offset: updatedOffset,
					scrollWidth,
					viewportWidth,
					scrollElements,
				};
			}),
		);
	};
